import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ── Thunks ──────────────────────────────────────────────────
export const get_seller_giveaways = createAsyncThunk(
    'sellerGiveaway/list',
    async ({ page = 1, limit = 20, status = '', approvalStatus = '' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('page', page)
            params.set('limit', limit)
            if (status) params.set('status', status)
            if (approvalStatus) params.set('approvalStatus', approvalStatus)
            const { data } = await api.get(`/seller/giveaways?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const create_seller_giveaway = createAsyncThunk(
    'sellerGiveaway/create',
    async (payload, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post('/seller/giveaways', payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_seller_giveaway = createAsyncThunk(
    'sellerGiveaway/getOne',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/seller/giveaways/${id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_seller_giveaway = createAsyncThunk(
    'sellerGiveaway/update',
    async ({ id, ...payload }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/seller/giveaways/${id}`, payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// Phase 6C — ownership-gated analytics. Aggregates only; never participant
// documents. Errors here must not block the rest of the detail page from
// rendering, so the slice stores the error and the UI shows a soft message.
export const get_seller_giveaway_analytics = createAsyncThunk(
    'sellerGiveaway/analytics',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/seller/giveaways/${id}/analytics`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

export const delete_seller_giveaway = createAsyncThunk(
    'sellerGiveaway/delete',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/seller/giveaways/${id}`)
            return fulfillWithValue({ id, message: data.message })
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ── Slice ───────────────────────────────────────────────────
const sellerGiveawaySlice = createSlice({
    name: 'sellerGiveaway',
    initialState: {
        giveaways:       [],
        total:           0,
        page:            1,
        totalPages:      1,
        currentGiveaway: null,
        loader:          false,
        submitLoader:    false,
        successMessage:  '',
        errorMessage:    '',
        // Phase 6C — analytics cache per detail page session.
        analytics:       null,
        analyticsLoader: false,
        analyticsError:  '',
    },
    reducers: {
        messageClear: (s) => { s.successMessage = ''; s.errorMessage = '' },
        // Clears analytics + current giveaway together so leaving the detail
        // page never leaks stale aggregates into the next session.
        clearCurrent: (s) => {
            s.currentGiveaway = null
            s.analytics = null
            s.analyticsError = ''
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_seller_giveaways.pending,   (s) => { s.loader = true })
            .addCase(get_seller_giveaways.fulfilled, (s, { payload }) => {
                s.loader = false
                s.giveaways = payload.giveaways || []
                s.total = payload.total || 0
                s.page = payload.page || 1
                s.totalPages = payload.totalPages || 1
            })
            .addCase(get_seller_giveaways.rejected,  (s) => { s.loader = false })

            .addCase(create_seller_giveaway.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(create_seller_giveaway.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Çəkiliş istəyi göndərildi'
                s.currentGiveaway = payload.giveaway || null
            })
            .addCase(create_seller_giveaway.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Xəta baş verdi'
            })

            .addCase(get_seller_giveaway.fulfilled, (s, { payload }) => {
                s.currentGiveaway = payload.giveaway || null
            })

            .addCase(update_seller_giveaway.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(update_seller_giveaway.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Yeniləndi'
                s.currentGiveaway = payload.giveaway || s.currentGiveaway
                const idx = s.giveaways.findIndex(g => g._id === payload.giveaway?._id)
                if (idx !== -1) s.giveaways[idx] = payload.giveaway
            })
            .addCase(update_seller_giveaway.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Yenilənmədi'
            })

            .addCase(delete_seller_giveaway.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Silindi'
                s.giveaways = s.giveaways.filter(g => g._id !== payload.id)
            })
            .addCase(delete_seller_giveaway.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Silinmədi'
            })

            // ── Phase 6C — analytics ───────────────────────────────────
            .addCase(get_seller_giveaway_analytics.pending,   (s) => {
                s.analyticsLoader = true
                s.analyticsError = ''
            })
            .addCase(get_seller_giveaway_analytics.fulfilled, (s, { payload }) => {
                s.analyticsLoader = false
                s.analytics = payload.analytics || null
            })
            .addCase(get_seller_giveaway_analytics.rejected,  (s, { payload }) => {
                s.analyticsLoader = false
                s.analytics = null
                s.analyticsError = payload?.error || payload?.message || 'Statistika yüklənmədi'
            })
    },
})

export const { messageClear, clearCurrent } = sellerGiveawaySlice.actions
export default sellerGiveawaySlice.reducer
